import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {Aangifte, BokkzIbDataService, IncomeTaxEntity} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {filter, Observable, shareReplay, take} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AsyncPipe} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-iack-info',
  templateUrl: './iack-info.component.html',
  styleUrls: ['./iack-info.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    AsyncPipe
  ]
})
export class IackInfoComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public isPartner = false;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    arbeidsInkomenPartner: new FormControl<number | null>(null),
    verzamelInkomenPartner: new FormControl<number | null>(null),
    geboortedatumPartner: new FormControl<Date | null>(null),
  });

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.dataService.calculateLatestIncomeTaxReturn().subscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: Aangifte): void {
    this.formGroup.reset({}, {emitEvent: false});
    this.formGroup.patchValue({arbeidsInkomenPartner: details.heffingskortingen?.arbeidsInkomenPartner || null}, {emitEvent: false});
    this.formGroup.patchValue({verzamelInkomenPartner: details.heffingskortingen?.verzamelInkomenPartner || null}, {emitEvent: false});
    this.formGroup.patchValue({geboortedatumPartner: details.partnerAangifte?.aangever.geboortedatum || null}, {emitEvent: false});
  }

  saveForm(): void {
    const form = this.formGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const update = (aangifte: any) => ({
        ...aangifte,
        heffingskortingen: {
          ...aangifte.heffingskortingen,
          geboortedatumPartner: form.geboortedatumPartner,
          arbeidsInkomenPartner: form.arbeidsInkomenPartner,
          verzamelInkomenPartner: form.verzamelInkomenPartner,
        }
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }
}
